import { z } from 'zod';
const maxClientIdLength = 2048;
const maxClientsPerPolicy = 64;
const maxMcpResources = 16;
const loopbackHosts = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);
const parseResourceUrl = (value) => {
    try {
        return new URL(value);
    }
    catch {
        // Invalid URLs are reported by the surrounding zod refinement.
    }
};
const resourceIndicatorGuard = z
    .string()
    .min(1)
    .max(maxClientIdLength)
    .superRefine((value, context) => {
    const url = parseResourceUrl(value);
    if (!url) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'resource must be an absolute URL',
        });
        return;
    }
    // Match the pinned provider's exact target validation: a trailing bare `#` has an empty
    // parsed hash, but the original href still carries a fragment component and is rejected.
    if (url.username || url.password || value.includes('#')) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'resource must not contain credentials or a fragment',
        });
    }
    const isLoopback = loopbackHosts.has(url.hostname.toLowerCase());
    if (url.protocol !== 'https:' && !(url.protocol === 'http:' && isLoopback)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'resource must use HTTPS; HTTP is allowed only for loopback hosts',
        });
    }
});
const clientIdGuard = z.string().min(1).max(maxClientIdLength);
export const mcpResourcePolicyGuard = z
    .object({
    enabled: z.boolean(),
    resource: resourceIndicatorGuard,
    inspectorClientId: clientIdGuard,
    browserClientIds: clientIdGuard.array().max(maxClientsPerPolicy),
    publicClientIds: clientIdGuard.array().max(maxClientsPerPolicy),
    allowCimd: z.boolean(),
})
    .superRefine(({ inspectorClientId, browserClientIds, publicClientIds }, context) => {
    const browserClients = new Set(browserClientIds);
    const publicClients = new Set(publicClientIds);
    const hasDuplicates = (values) => new Set(values).size !== values.length;
    if (hasDuplicates(browserClientIds) || hasDuplicates(publicClientIds)) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: 'client lists must be unique' });
    }
    if (browserClients.has(inspectorClientId) || publicClients.has(inspectorClientId)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'inspectorClientId must not also be a request client',
        });
    }
    if (browserClientIds.some((clientId) => publicClients.has(clientId))) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'browser and public client lists must be disjoint',
        });
    }
});
export const nativeMcpRegistrationGuard = z.object({
    resource: resourceIndicatorGuard,
    clientId: clientIdGuard,
});
export const mcpConfigGuard = z
    .object({
    resources: mcpResourcePolicyGuard.array().max(maxMcpResources),
    nativeRegistration: nativeMcpRegistrationGuard.optional(),
})
    .superRefine(({ resources, nativeRegistration }, context) => {
    if (new Set(resources.map(({ resource }) => resource)).size !== resources.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'trusted MCP resources must be unique',
        });
    }
    if (!nativeRegistration) {
        return;
    }
    const policy = resources.find(({ resource }) => resource === nativeRegistration.resource);
    if (!policy) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['nativeRegistration', 'resource'],
            message: 'native registration resource must be an MCP resource policy',
        });
        return;
    }
    if (!policy.enabled) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['nativeRegistration', 'resource'],
            message: 'native registration resource must be enabled',
        });
    }
    const { clientId } = nativeRegistration;
    if (policy.inspectorClientId === clientId || policy.browserClientIds.includes(clientId)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['nativeRegistration', 'clientId'],
            message: 'native registration client must not be an inspector or browser client',
        });
    }
    if (!policy.publicClientIds.includes(clientId)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['nativeRegistration', 'clientId'],
            message: 'native registration client must be listed as a public client',
        });
    }
});
export const defaultMcpConfig = Object.freeze({ resources: [] });
